import React, { FC, useMemo, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { RecoilRoot, useRecoilState } from 'recoil'
import timezone from 'dayjs/plugin/timezone'
import { addStickyProps } from 'utils/analytics'
import { usePageLoad } from 'store/hooks'
import OverlayMount from 'layouts/components/OverlayMount'
import PageTitle from 'components/PageTitle'
import { Calendar } from './types'
import { homeownerSiteVisitCalendarState } from './state'
import NotFound from './NotFound'
import Canceled from './Canceled'
import Scheduled from './Scheduled'
import Reschedule from './Reschedule'
import Logo from 'svgs/logo'

dayjs.extend(utc)
dayjs.extend(timezone)

interface HomeownerSiteVisitProps {
  calendar: Calendar
}

const HomeownerSiteVisit: FC<HomeownerSiteVisitProps> = ({ calendar }) => {
  const [rescheduling, setRescheduling] = useState(false)
  const [reschedulingRequestSent, setReschedulingRequestSent] = useState(false)

  useEffect(() => {
    if (!calendar.impersonated) return

    addStickyProps({ impersonated: calendar.impersonated }, true)
  }, [calendar.impersonated])

  const event = useMemo(() => {
    return calendar.events.find((event) => calendar.target_event_id == event.id)
  }, [calendar])

  const status: 'unscheduled' | 'scheduled' | 'canceled' | 'declined' = useMemo(() => {
    if (event) {
      if (event.status == 'canceled') return 'canceled'
      if (event.status == 'declined') return 'declined'

      if (['scheduled', 'occurring', 'completed'].includes(event.status)) return 'scheduled'
    }

    return 'unscheduled' // Our default.
  }, [event])

  // Scroll to the top of the page any time our status changes.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' } as any)
    } catch {
      // Fallback in case the above isn't supported.
      window.scrollTo(0, 0)
    }
  }, [status])

  // Any time our calendar changes, update CSRF
  useEffect(() => {
    if (!window) return
    document.querySelector('meta[name=csrf-param]')?.setAttribute('content', calendar.csrf_param)
    document.querySelector('meta[name=csrf-token]')?.setAttribute('content', calendar.csrf_token)
  }, [calendar])

  usePageLoad('homeowner-site-visit-scheduler')

  return (
    <OverlayMount>
      <PageTitle title="Schedule Site Visit" />
      <div className="tw:flex tw:flex-col tw:min-h-screen">
        {calendar.impersonated && (
          <div className="tw:bg-red-200 tw:text-red-900 tw:text-center tw:p-4 tw:border-y-2 tw:border-red-600">
            {'You are now impersonating '}
            {<strong>{calendar.property.email}</strong>}
          </div>
        )}
        <div className="tw:flex tw:flex-col tw:grow tw:relative tw:bg-neutrals-100 tw:mx-4 tw:pb-10">
          <div className="tw:flex tw:flex-col tw:grow tw:relative tw:pt-4 tw:md:w-lg tw:md:mx-auto tw:md:pt-6 tw:md:pb-12">
            <Logo className="tw:h-9 tw:w-36 tw:mx-auto tw:px-6 tw:text-green-700 tw:md:h-14 tw:mb-4" />
            {!event || !event.contact || ['occurring', 'completed'].includes(event.status) ? (
              <NotFound calendar={calendar} />
            ) : ['canceled', 'declined'].includes(event.status) ? (
              <Canceled calendar={calendar} event={event} />
            ) : rescheduling ? (
              <Reschedule
                calendar={calendar}
                event={event}
                setRescheduling={setRescheduling}
                setReschedulingRequestSent={setReschedulingRequestSent}
              />
            ) : !rescheduling ? (
              <Scheduled
                calendar={calendar}
                event={event}
                setRescheduling={setRescheduling}
                reschedulingRequestSent={reschedulingRequestSent}
              />
            ) : null}
          </div>
        </div>
      </div>
    </OverlayMount>
  )
}

const WrappedHomeownerSiteVisit: FC<HomeownerSiteVisitProps> = (props) => {
  const [calendar, setCalendar] = useRecoilState(homeownerSiteVisitCalendarState)

  useEffect(() => {
    setCalendar(props.calendar)
  }, [props.calendar, setCalendar])

  if (calendar == null) return null

  return <HomeownerSiteVisit calendar={calendar} />
}

const Wrapped2HomeownerSiteVisit: FC<HomeownerSiteVisitProps> = (props) => {
  return (
    <RecoilRoot>
      <WrappedHomeownerSiteVisit {...props} />
    </RecoilRoot>
  )
}

export default Wrapped2HomeownerSiteVisit
